import { useState, useEffect } from "react";
import {
  Container, Header, Table, Button, SpaceBetween, Box, Badge,
  Modal, FormField, Input, Textarea, Multiselect, Alert, Pagination, TextFilter,
} from "@cloudscape-design/components";
import { adminService } from "../../services/adminService";

export default function GroupManagement() {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", description: "" });
  const [creating, setCreating] = useState(false);

  // Edit modal
  const [editGroup, setEditGroup] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", description: "" });
  const [editing, setEditing] = useState(false);

  // Users modal
  const [userGroup, setUserGroup] = useState(null);
  const [groupUsers, setGroupUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [savingUsers, setSavingUsers] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [g, u] = await Promise.all([adminService.getGroups(), adminService.getUsers()]);
      setGroups(g);
      setUsers(u);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load groups.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createGroup = async () => {
    if (!createForm.name.trim()) { setError("Group name required."); return; }
    setCreating(true);
    try {
      await adminService.createGroup(createForm.name.trim(), createForm.description.trim());
      setSuccess(`Group "${createForm.name}" created.`);
      setShowCreate(false);
      setCreateForm({ name: "", description: "" });
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create group.");
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (group) => {
    setEditGroup(group);
    setEditForm({ name: group.name, description: group.description });
  };

  const saveEdit = async () => {
    setEditing(true);
    try {
      await adminService.updateGroup(editGroup.group_id, editForm.name, editForm.description);
      setSuccess("Group updated.");
      setEditGroup(null);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to update group.");
    } finally {
      setEditing(false);
    }
  };

  const deleteGroup = async (groupId, name) => {
    if (!window.confirm(`Delete group "${name}"?`)) return;
    try {
      await adminService.deleteGroup(groupId);
      setSuccess(`Group "${name}" deleted.`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to delete group.");
    }
  };

  const openUsers = async (group) => {
    setUserGroup(group);
    const gu = await adminService.getGroupUsers(group.group_id);
    setGroupUsers(gu);
    setSelectedUsers(gu.map(u => ({ value: u.user_id, label: u.email })));
  };

  const saveGroupUsers = async () => {
    setSavingUsers(true);
    try {
      const currentIds = new Set(groupUsers.map(u => u.user_id));
      const newIds = new Set(selectedUsers.map(u => u.value));

      const toAdd = [...newIds].filter(id => !currentIds.has(id));
      const toRemove = [...currentIds].filter(id => !newIds.has(id));

      for (const userId of toAdd) {
        const existing = await adminService.getUserGroups(userId);
        const ids = existing.map(g => g.group_id || g.name);
        if (!ids.includes(userGroup.group_id)) {
          await adminService.updateUserGroups(userId, [...ids, userGroup.group_id]);
        }
      }
      for (const userId of toRemove) {
        const existing = await adminService.getUserGroups(userId);
        const ids = existing.map(g => g.group_id || g.name).filter(id => id !== userGroup.group_id);
        await adminService.updateUserGroups(userId, ids);
      }

      setSuccess(`Users updated for group "${userGroup.name}".`);
      setUserGroup(null);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to update group users.");
    } finally {
      setSavingUsers(false);
    }
  };

  const filtered = groups.filter(g => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return g.name?.toLowerCase().includes(q) || g.description?.toLowerCase().includes(q);
  });
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const userOptions = users.map(u => ({ value: u.user_id, label: u.email }));

  const cols = [
    {
      id: "name", header: "Group Name",
      cell: g => <SpaceBetween size="xs" direction="vertical">
        <Box fontWeight="bold">{g.name}</Box>
        <Box fontSize="body-s" color="text-status-subdued">{g.description}</Box>
      </SpaceBetween>,
    },
    { id: "users", header: "Users", cell: g => <Badge color={g.user_count > 0 ? "blue" : "grey"}>{g.user_count} user{g.user_count !== 1 ? "s" : ""}</Badge> },
    { id: "created", header: "Created", cell: g => g.created_at ? new Date(g.created_at).toLocaleDateString() : "—" },
    {
      id: "actions", header: "Actions",
      cell: g => (
        <SpaceBetween direction="horizontal" size="xs">
          <Button size="small" onClick={() => openEdit(g)}>Edit</Button>
          <Button size="small" onClick={() => openUsers(g)}>Users</Button>
          <Button size="small" disabled={g.user_count > 0} onClick={() => deleteGroup(g.group_id, g.name)}>Delete</Button>
        </SpaceBetween>
      ),
    },
  ];

  return (
    <Container header={
      <Header variant="h2" description="Create and manage user groups"
        actions={<SpaceBetween direction="horizontal" size="xs">
          <Button onClick={load} loading={loading}>Refresh</Button>
          <Button variant="primary" onClick={() => setShowCreate(true)}>Create Group</Button>
        </SpaceBetween>}>
        Group Management
      </Header>
    }>
      <SpaceBetween size="m">
        {error && <Alert type="error" dismissible onDismiss={() => setError("")}>{error}</Alert>}
        {success && <Alert type="success" dismissible onDismiss={() => setSuccess("")}>{success}</Alert>}

        <Table
          columnDefinitions={cols}
          items={paginated}
          loading={loading}
          loadingText="Loading groups..."
          filter={<TextFilter filteringText={filter} onChange={({ detail }) => { setFilter(detail.filteringText); setPage(1); }} filteringPlaceholder="Search groups..." />}
          pagination={<Pagination currentPageIndex={page} pagesCount={Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))} onChange={({ detail }) => setPage(detail.currentPageIndex)} />}
          empty={<Box textAlign="center" color="text-body-secondary">No groups yet.<br /><Button variant="primary" onClick={() => setShowCreate(true)}>Create Group</Button></Box>}
        />

        {/* Create Modal */}
        <Modal visible={showCreate} onDismiss={() => setShowCreate(false)} header="Create New Group"
          footer={<SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button variant="primary" onClick={createGroup} loading={creating}>Create</Button>
          </SpaceBetween>}>
          <SpaceBetween size="m">
            <FormField label="Group Name"><Input value={createForm.name} onChange={({ detail }) => setCreateForm(f => ({ ...f, name: detail.value }))} placeholder="e.g. boxer" /></FormField>
            <FormField label="Description"><Textarea value={createForm.description} onChange={({ detail }) => setCreateForm(f => ({ ...f, description: detail.value }))} rows={3} placeholder="Optional description" /></FormField>
          </SpaceBetween>
        </Modal>

        {/* Edit Modal */}
        <Modal visible={!!editGroup} onDismiss={() => setEditGroup(null)} header={`Edit Group: ${editGroup?.name}`}
          footer={<SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => setEditGroup(null)}>Cancel</Button>
            <Button variant="primary" onClick={saveEdit} loading={editing}>Save</Button>
          </SpaceBetween>}>
          <SpaceBetween size="m">
            <FormField label="Group Name"><Input value={editForm.name} onChange={({ detail }) => setEditForm(f => ({ ...f, name: detail.value }))} /></FormField>
            <FormField label="Description"><Textarea value={editForm.description} onChange={({ detail }) => setEditForm(f => ({ ...f, description: detail.value }))} rows={3} /></FormField>
          </SpaceBetween>
        </Modal>

        {/* Users Modal */}
        <Modal visible={!!userGroup} onDismiss={() => setUserGroup(null)} header={`Users in Group: ${userGroup?.name}`}
          footer={<SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => setUserGroup(null)}>Cancel</Button>
            <Button variant="primary" onClick={saveGroupUsers} loading={savingUsers}>Save Users</Button>
          </SpaceBetween>}>
          <FormField label="Users" description="Select users that belong to this group">
            <Multiselect selectedOptions={selectedUsers} onChange={({ detail }) => setSelectedUsers(detail.selectedOptions)} options={userOptions} placeholder="Select users..." />
          </FormField>
        </Modal>
      </SpaceBetween>
    </Container>
  );
}
